import React, { useState, useEffect } from 'react';
import { FiX, FiCalendar } from 'react-icons/fi';
import useMonitoringHistory from './hooks/useMonitoringHistory';
import APaginationInline from '../../molecules/APaginationInline';
import SimpleCalendar from '../../atoms/SimpleCalendar';
import Dialog from '../../organisms/Dialog';
import { Classification, HistoryItem } from '../../../services/monitoringApi';

interface MonitoringHistoryDrawerProps {
    isOpen: boolean;
    item: Classification;
    onClose: () => void;
}

const OPERATION_LABELS: Record<string, string> = {
    MONITORING_EXECUTION: 'Execução de Monitoramento',
    RISK_LEVEL_UPDATED: 'Alteração de Risco',
    CLASSIFICATION_CREATED: 'Cadastro',
    CLASSIFICATION_DEACTIVATED: 'Desativação',
};

const RESULT_BADGE: Record<string, { bg: string; label: string }> = {
    SUCESSO: { bg: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Sucesso' },
    SUCCESS: { bg: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Sucesso' },
    FALHA: { bg: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Falha' },
    FAILURE: { bg: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Falha' },
    TIMEOUT: { bg: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: 'Timeout' },
};

const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const HistoryItemCard: React.FC<{ entry: HistoryItem; onClick: () => void }> = ({ entry, onClick }) => {
    const details = entry.details || {};
    // AF-3219: New format uses details.divergence (object with has_divergence, added_rules, removed_rules)
    // Legacy format used details.divergences (array of {field, previous_value, current_value})
    const divergence = details.divergence as { has_divergence?: boolean; added_rules?: string[]; removed_rules?: string[] } | undefined;
    const hasDivergenceFlag = divergence?.has_divergence || false;
    const rulesChanged = (divergence?.added_rules?.length || 0) + (divergence?.removed_rules?.length || 0);
    // Fallback to legacy
    const legacyCount = (details.divergences as unknown[])?.length || 0;
    const showDivergence = hasDivergenceFlag || legacyCount > 0;
    const divergenceLabel = hasDivergenceFlag
        ? (rulesChanged > 0
            ? `${rulesChanged} regra${rulesChanged !== 1 ? 's' : ''} alterada${rulesChanged !== 1 ? 's' : ''}`
            : 'Recomendação alterada')
        : `${legacyCount} divergência${legacyCount !== 1 ? 's' : ''} detectada${legacyCount !== 1 ? 's' : ''}`;
    const resultInfo = RESULT_BADGE[entry.operation_result?.toUpperCase()] || RESULT_BADGE['SUCESSO'];

    const isRiskUpdate = entry.operation_type === 'RISK_LEVEL_UPDATED';
    const oldRisk = details.old_risk_level as string | undefined;
    const newRisk = details.new_risk_level as string | undefined;
    const notes = details.notes as string | undefined;

    return (
        <div
            className="border-l-2 border-blue-300 dark:border-blue-600 pl-4 pb-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 rounded-r-md -ml-0.5 transition-colors"
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
        >
            <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDateTime(entry.created_at)}
                </span>
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${resultInfo.bg}`}>
                    {resultInfo.label}
                </span>
            </div>

            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {OPERATION_LABELS[entry.operation_type] || entry.operation_type}
            </p>

            {isRiskUpdate && oldRisk && newRisk && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {oldRisk} &rarr; {newRisk}
                </p>
            )}

            {!isRiskUpdate && showDivergence && (
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    {divergenceLabel}
                </p>
            )}

            {notes && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                    &ldquo;{notes}&rdquo;
                </p>
            )}

            {entry.user_id && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    por {entry.user_id}
                </p>
            )}
        </div>
    );
};

// Inline date filter for the history drawer (uses SimpleCalendar)
const HistoryDateFilter: React.FC<{
    dateFrom: string;
    dateTo: string;
    onChangeFrom: (v: string) => void;
    onChangeTo: (v: string) => void;
}> = ({ dateFrom, dateTo, onChangeFrom, onChangeTo }) => {
    const [openPicker, setOpenPicker] = useState<'from' | 'to' | null>(null);

    const dateToISO = (d: Date): string => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const parseISO = (str: string): Date | null => {
        if (!str) return null;
        const [y, m, d] = str.split('-').map(Number);
        return new Date(y, m - 1, d);
    };

    const formatDisplay = (str: string): string => {
        if (!str) return '—';
        const [y, m, d] = str.split('-');
        return `${d}/${m}/${y}`;
    };

    return (
        <div className="flex gap-2 items-end">
            <div className="flex-1 relative">
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">De</label>
                <button
                    type="button"
                    onClick={() => setOpenPicker(openPicker === 'from' ? null : 'from')}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 hover:border-blue-400"
                >
                    <FiCalendar size={12} className="text-gray-400" />
                    <span>{formatDisplay(dateFrom)}</span>
                </button>
                {openPicker === 'from' && (
                    <div className="absolute z-20 mt-1 left-0">
                        <SimpleCalendar
                            selectedDate={parseISO(dateFrom)}
                            onDateChange={(d) => { onChangeFrom(dateToISO(d)); setOpenPicker(null); }}
                        />
                    </div>
                )}
            </div>
            <div className="flex-1 relative">
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Até</label>
                <button
                    type="button"
                    onClick={() => setOpenPicker(openPicker === 'to' ? null : 'to')}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 hover:border-blue-400"
                >
                    <FiCalendar size={12} className="text-gray-400" />
                    <span>{formatDisplay(dateTo)}</span>
                </button>
                {openPicker === 'to' && (
                    <div className="absolute z-20 mt-1 right-0">
                        <SimpleCalendar
                            selectedDate={parseISO(dateTo)}
                            onDateChange={(d) => { onChangeTo(dateToISO(d)); setOpenPicker(null); }}
                        />
                    </div>
                )}
            </div>
            {(dateFrom || dateTo) && (
                <button
                    type="button"
                    onClick={() => { onChangeFrom(''); onChangeTo(''); }}
                    className="text-xs text-gray-500 hover:text-red-500 pb-1"
                    title="Limpar datas"
                >
                    ✕
                </button>
            )}
        </div>
    );
};

const MonitoringHistoryDrawer: React.FC<MonitoringHistoryDrawerProps> = ({ isOpen, item, onClose }) => {
    const {
        items, total, page, setPage, totalPages, isLoading,
        dateFrom, setDateFrom, dateTo, setDateTo,
        hasDivergence, setHasDivergence,
    } = useMonitoringHistory(isOpen ? item.person_id : null);

    const [detailEntry, setDetailEntry] = useState<HistoryItem | null>(null);

    // Reset detail modal when drawer closes or person changes
    useEffect(() => {
        setDetailEntry(null);
    }, [isOpen, item.person_id]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-40 flex justify-end">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />

            {/* Drawer */}
            <div className="relative w-full max-w-md bg-white dark:bg-slate-800 shadow-xl flex flex-col h-full z-50">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            Histórico
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {item.person_id}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600"
                        aria-label="Fechar"
                    >
                        <FiX className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>
                </div>

                {/* Filters */}
                <div className="px-5 py-3 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
                    <HistoryDateFilter
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onChangeFrom={setDateFrom}
                        onChangeTo={setDateTo}
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer mt-2">
                        <input
                            type="checkbox"
                            checked={hasDivergence === true}
                            onChange={(e) => setHasDivergence(e.target.checked ? true : undefined)}
                            className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        Apenas com divergência
                    </label>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        </div>
                    ) : items.length === 0 ? (
                        <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
                            Nenhum registro encontrado
                        </p>
                    ) : (
                        <div className="space-y-1">
                            {items.map((entry) => (
                                <HistoryItemCard key={entry.id} entry={entry} onClick={() => setDetailEntry(entry)} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer pagination */}
                {totalPages > 1 && (
                    <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {total} registro{total !== 1 ? 's' : ''}
                        </span>
                        <APaginationInline
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                        />
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {detailEntry && (
                <HistoryDetailModal entry={detailEntry} onClose={() => setDetailEntry(null)} />
            )}
        </div>
    );
};

// ============================================================
// Detail Modal — shows full provider_data + divergences
// ============================================================

const PROVIDER_FIELD_LABELS: Record<string, string> = {
    name: 'Nome',
    analysisStatus: 'Status da Análise',
    analysis_status: 'Status da Análise',
    bacenQgiCpf: 'BACEN QGI',
    bacen_qgi_cpf: 'BACEN QGI',
    pepSearchV2: 'PEP',
    pep_search_v2: 'PEP',
    pepReason: 'PEP Motivo',
    pep_reason: 'PEP Motivo',
    sanctionsList: 'OFAC',
    sanctions_list: 'OFAC',
    euSanctionList: 'Sanções UE',
    eu_sanction_list: 'Sanções UE',
    frSanctionList: 'Sanções França',
    fr_sanction_list: 'Sanções França',
    ukSanctionList: 'Sanções UK',
    uk_sanction_list: 'Sanções UK',
    onuSanctionLists: 'Sanções ONU',
    onu_sanction_lists: 'Sanções ONU',
    usNonproliferationSanctionList: 'Sanções EUA',
    us_nonproliferation_sanction_list: 'Sanções EUA',
    redNoticeInterpol: 'Interpol',
    red_notice_interpol: 'Interpol',
    ceafInfosGovernmentBase: 'CEAF',
    ceaf_infos_government_base: 'CEAF',
    ceisInfosGovernmentBaseCpf: 'CEIS',
    ceis_infos_government_base_cpf: 'CEIS',
    administrativeImprobityCpf: 'CNJ',
    administrative_improbity_cpf: 'CNJ',
    criminalRecordFederalPolice: 'Antecedentes PF',
    criminal_record_federal_police: 'Antecedentes PF',
    workSlaveCpf: 'Trabalho Escravo',
    work_slave_cpf: 'Trabalho Escravo',
    workSlaveCnpj: 'Trabalho Escravo',
    work_slave_cnpj: 'Trabalho Escravo',
    negativeMedia: 'Mídia Negativa',
    midia_negativa: 'Mídia Negativa',
    courtWarrantBnmpCpf: 'BNMP',
    court_warrant_bnmp_cpf: 'BNMP',
    ibamaNegativeCertificateCpf: 'IBAMA Certidão',
    ibama_negative_certificate_cpf: 'IBAMA Certidão',
    ibamaAssessmentCpf: 'IBAMA Multas',
    ibama_assessment_cpf: 'IBAMA Multas',
    ibamaEmbargoCpf: 'IBAMA Embargos',
    ibama_embargo_cpf: 'IBAMA Embargos',
};

const formatFieldValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (value === true) return 'Sim';
    if (value === false) return 'Não';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
};

const HistoryDetailModal: React.FC<{ entry: HistoryItem; onClose: () => void }> = ({ entry, onClose }) => {
    const details = entry.details || {};
    const providerData = (details.provider_data || {}) as Record<string, unknown>;
    const isBaseline = details.is_baseline as boolean | undefined;

    // AF-3219: New format — rule-based divergence
    const divergence = details.divergence as {
        has_divergence?: boolean;
        added_rules?: string[];
        removed_rules?: string[];
        recommendation_changed?: boolean;
        previous_recommendation?: string;
        current_recommendation?: string;
    } | undefined;
    const hasDivergenceNew = divergence?.has_divergence || false;

    // Legacy format — field-based divergence
    const divergencesLegacy = (details.divergences || []) as Array<{ field: string; previous_value: unknown; current_value: unknown }>;

    // Recommendation data
    const recommendationData = (details.recommendation_data || {}) as {
        recommendation?: string;
        rules_applied?: string[];
        risk_score?: number;
        pld_flag?: boolean;
    };

    // Extract relevant fields
    const status = (providerData.displayStatus || providerData.status || '—') as string;
    const document = (providerData.document || '—') as string;
    const reportType = (providerData.reportType || '—') as string;
    const extractedPayload = (providerData.extractedPayload || {}) as Record<string, unknown>;

    const body = (
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Summary info */}
            <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">Status</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{status}</span>
                </div>
                <div>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">Documento</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{document}</span>
                </div>
                <div>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">Tipo de Relatório</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{reportType}</span>
                </div>
            </div>

            {/* Recommendation Result */}
            {recommendationData.recommendation && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Resultado do Recommendation Engine
                    </h4>
                    <div className="flex items-center gap-3 text-sm">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            recommendationData.recommendation === 'REPROVADO' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                            recommendationData.recommendation === 'PENDENTE' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                            'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                            {recommendationData.recommendation}
                        </span>
                        {recommendationData.pld_flag && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                PLD
                            </span>
                        )}
                    </div>
                    {recommendationData.rules_applied && recommendationData.rules_applied.length > 0 && (
                        <div className="mt-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Regras acionadas:</span>
                            <ul className="mt-1 space-y-0.5">
                                {recommendationData.rules_applied.map((rule, idx) => (
                                    <li key={idx} className="text-xs text-gray-700 dark:text-gray-300 pl-2 border-l-2 border-blue-300">
                                        {rule}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Divergências de Regras (novo formato AF-3219) */}
            <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Divergências de Regras
                </h4>
                {hasDivergenceNew ? (
                    <div className="space-y-2">
                        {divergence?.recommendation_changed && (
                            <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded text-sm">
                                <span className="font-medium">Recomendação alterada: </span>
                                <span className="text-red-600 dark:text-red-400">{divergence.previous_recommendation}</span>
                                <span> &rarr; </span>
                                <span className="text-green-600 dark:text-green-400">{divergence.current_recommendation}</span>
                            </div>
                        )}
                        {divergence?.added_rules && divergence.added_rules.length > 0 && (
                            <div className="p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded">
                                <span className="text-xs font-medium text-orange-700 dark:text-orange-300">Regras novas acionadas:</span>
                                <ul className="mt-1 space-y-0.5">
                                    {divergence.added_rules.map((rule, idx) => (
                                        <li key={idx} className="text-xs text-orange-800 dark:text-orange-200 pl-2">+ {rule}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {divergence?.removed_rules && divergence.removed_rules.length > 0 && (
                            <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded">
                                <span className="text-xs font-medium text-green-700 dark:text-green-300">Regras que deixaram de ser acionadas:</span>
                                <ul className="mt-1 space-y-0.5">
                                    {divergence.removed_rules.map((rule, idx) => (
                                        <li key={idx} className="text-xs text-green-800 dark:text-green-200 pl-2">- {rule}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {!divergence?.recommendation_changed && !(divergence?.added_rules?.length) && !(divergence?.removed_rules?.length) && (
                            <p className="text-sm text-orange-600 dark:text-orange-400 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded">
                                Divergência detectada nas regras do Recommendation Engine.
                            </p>
                        )}
                    </div>
                ) : divergencesLegacy.length > 0 ? (
                    /* Legacy format fallback */
                    <div className="border border-red-200 dark:border-red-700 rounded overflow-hidden">
                        <table className="min-w-full text-xs">
                            <thead className="bg-red-50 dark:bg-red-900/30">
                                <tr>
                                    <th className="px-3 py-2 text-left font-medium">Campo</th>
                                    <th className="px-3 py-2 text-left font-medium">Anterior</th>
                                    <th className="px-3 py-2 text-left font-medium">Atual</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-red-100 dark:divide-red-800">
                                {divergencesLegacy.map((d, idx) => (
                                    <tr key={idx}>
                                        <td className="px-3 py-2 font-medium">{PROVIDER_FIELD_LABELS[d.field] || d.field}</td>
                                        <td className="px-3 py-2 text-red-600 dark:text-red-400">{formatFieldValue(d.previous_value)}</td>
                                        <td className="px-3 py-2 text-green-600 dark:text-green-400">{formatFieldValue(d.current_value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded">
                        Sem divergências encontradas{isBaseline ? ' (primeira consulta — baseline)' : ''}
                    </p>
                )}
            </div>

            {/* Extracted Payload */}
            {Object.keys(extractedPayload).length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Dados Extraídos (Inovamind)
                    </h4>
                    <div className="border border-gray-200 dark:border-slate-600 rounded overflow-hidden">
                        <table className="min-w-full text-xs">
                            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                {Object.entries(extractedPayload)
                                    .map(([key, value]) => (
                                        <tr key={key} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                                            <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300 w-1/3 align-top">
                                                {PROVIDER_FIELD_LABELS[key] || key}
                                            </td>
                                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                                {formatFieldValue(value)}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <Dialog
            id="history-detail-modal"
            show={true}
            onClose={onClose}
            header="Detalhes da Execução"
            body={body}
            size="lg"
        />
    );
};

export default MonitoringHistoryDrawer;
